import * as React from 'react';
import { cn } from '../../lib';

export interface FormFieldProps {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
  htmlFor?: string;
}

const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  ({ className, label, description, error, required, children, htmlFor, ...props }, ref) => {
    const errorId = React.useId();
    const descriptionId = React.useId();
    const describedBy = error ? errorId : description ? descriptionId : undefined;
    const control = React.isValidElement(children)
      ? React.cloneElement(children, {
          'aria-describedby': describedBy,
          'aria-invalid': error ? true : undefined,
        })
      : children;

    return (
      <div ref={ref} className={cn('space-y-1.5', className)} {...props}>
        {label && (
          <label
            htmlFor={htmlFor}
            className={cn(
              'block text-sm font-medium text-foreground',
              required && 'after:content-["*"] after:ml-0.5 after:text-destructive'
            )}
          >
            {label}
          </label>
        )}
        <div className="relative">{control}</div>
        {description && !error && (
          <p id={descriptionId} className="text-sm text-muted-foreground">
            {description}
          </p>
        )}
        {error && (
          <p id={errorId} className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);
FormField.displayName = 'FormField';

export { FormField };
